import { runWithLoading } from '../../../../utils/redux/loadingOperation'
import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import v4 from 'uuid/v4'

import { completeTaskWithMotion } from '../../../TaskListView/TaskItem/TaskPresentation/taskCompletionHandoff'
import { getWorkflowStepsIdsSorted } from '../../../../utils/HelperFunctions'
import {
    moveTasksFromDone,
    moveTasksFromMiddleOfWorkflow,
    moveTasksFromOpen,
} from '../../../../utils/backends/Tasks/tasksFirestore'
import { DONE_STEP, OPEN_STEP } from '../../../TaskListView/Utils/TasksHelper'
import { taskUsesWorkflowOutsideSteps } from './commentPopupWorkflowState'
import MainButtons from '../../../WorkflowModal/MainButtons'
import { WORKFLOW_BACKWARD } from '../../../WorkflowModal/workflowDirections'
import { getWorkflowStepName } from '../../../WorkflowModal/workflowNavigation'
import Icon from '../../../Icon'
import styles, { colors } from '../../../styles/global'
import { translate } from '../../../../i18n/TranslationService'
import useOnHover from '../../../../hooks/UseOnHover'

const CHAT_APPEARANCE = 'chat'

function WorkflowStepOption({ step, controlsDisabled, moveTaskToStep, chatAppearance, narrow }) {
    const { hover, onHover, offHover } = useOnHover()
    const optionDisabled = controlsDisabled || step.current

    return (
        <TouchableOpacity
            testID={step.current ? 'comment-popup-current-workflow-step' : undefined}
            style={[
                localStyles.stepOption,
                chatAppearance && localStyles.chatStepOption,
                chatAppearance && hover && !optionDisabled && localStyles.chatStepOptionHover,
                step.current && localStyles.currentStepOption,
                step.current && chatAppearance && localStyles.chatCurrentStepOption,
            ]}
            onPress={() => moveTaskToStep(step.id, 'selector')}
            onMouseEnter={onHover}
            onMouseLeave={offHover}
            disabled={optionDisabled}
            accessibilityRole="button"
            accessibilityLabel={translate('Select workflow step with name', { step: step.label })}
            accessibilityState={{ selected: step.current, disabled: optionDisabled }}
        >
            <Text
                style={[styles.subtitle1, localStyles.stepOptionText, chatAppearance && localStyles.chatStepOptionText]}
                numberOfLines={narrow ? 3 : 2}
            >
                {step.label}
            </Text>
            {step.current && (
                <View style={localStyles.currentStepIndicator}>
                    <Icon name="check" size={14} color={colors.Primary100} />
                    <Text style={[styles.caption2, localStyles.currentStepText]}>{translate('Current')}</Text>
                </View>
            )}
        </TouchableOpacity>
    )
}

/**
 * AT-2501 — the popup shows the workflow in all three states a task can be in, not just the
 * middle one. `currentStepId` is the id the step LIST highlights and the selector names, so it is
 * the position itself (`OPEN_STEP` / `DONE_STEP`) rather than the last entry of `stepHistory`:
 * a completed task's history still ends on the last step it was reviewed at, which is where it
 * goes BACK to, not where it is.
 *
 * `forwardStepId` is null in exactly one case — Done, which is terminal — and that is what hides
 * the forward button. Open has the mirror-image absence and has always expressed it as
 * `backwardStepId: null`; `MainButtons` was already hiding the backward button there.
 */
export const getCommentPopupWorkflowTargets = (task, workflow) => {
    const stepIds = getWorkflowStepsIdsSorted(workflow)
    if (stepIds.length === 0) return null

    const stepHistory = Array.isArray(task?.stepHistory) ? task.stepHistory : []
    const lastStepId = stepHistory[stepHistory.length - 1]
    const currentStep = stepIds.indexOf(lastStepId)

    if (task?.done) {
        if (!taskUsesWorkflowOutsideSteps(task)) return null

        return {
            currentStep: DONE_STEP,
            currentStepId: DONE_STEP,
            stepIds,
            // One step back from Done is the step the task was last reviewed at — or Open, when it
            // was completed without ever entering the workflow.
            backwardStepId: currentStep >= 0 ? lastStepId : OPEN_STEP,
            forwardStepId: null,
        }
    }

    if (currentStep < 0) {
        // Not standing on a step. Either the task has not entered the workflow yet (`OPEN_STEP`,
        // which is what every task is created with) or it stands on a step that no longer exists,
        // which stays unrepresentable exactly as it was before.
        const isOpenTask = stepHistory.length === 0 || lastStepId === OPEN_STEP
        if (!isOpenTask) return null
        // An assistant workflow task is BORN into the workflow, so it is offered its steps
        // regardless of the flags below — that is the AT-2146 behaviour and it stays untouched.
        if (task?.workflowTask !== true && !taskUsesWorkflowOutsideSteps(task)) return null

        return {
            currentStep: OPEN_STEP,
            currentStepId: OPEN_STEP,
            stepIds,
            backwardStepId: null,
            forwardStepId: stepIds[0],
        }
    }

    return {
        currentStep,
        currentStepId: lastStepId,
        stepIds,
        backwardStepId: currentStep > 0 ? stepIds[currentStep - 1] : OPEN_STEP,
        forwardStepId: currentStep + 1 < stepIds.length ? stepIds[currentStep + 1] : DONE_STEP,
    }
}

export const getCommentPopupSelectableSteps = (task, workflow) => {
    const targets = getCommentPopupWorkflowTargets(task, workflow)
    if (!targets) return []

    return [
        { id: OPEN_STEP, label: translate('Open') },
        ...targets.stepIds.map(id => ({ id, label: workflow[id].description })),
        { id: DONE_STEP, label: translate('Done') },
    ].map(step => ({ ...step, current: step.id === targets.currentStepId }))
}

export default function CommentPopupWorkflowControls({
    projectId,
    task,
    workflow,
    disabled,
    onDirectionalTransitionSuccess,
    appearance,
    narrow = false,
    /**
     * AT-2495 — the row's completion animation, passed in by `TaskPresentation` when these
     * controls sit under the comment-popup task header. Absent in `TaskChatWorkflowControls`
     * (the task detailed view), where there is no row to animate and the write goes out
     * immediately, exactly as it does today.
     */
    completionMotion,
}) {
    const [submitting, setSubmitting] = useState(false)
    const [selectorOpen, setSelectorOpen] = useState(false)
    const { hover: selectorHovered, onHover: onSelectorHover, offHover: offSelectorHover } = useOnHover()
    const submittingRef = useRef(false)
    const checkBoxIdRef = useRef(v4())
    const targets = getCommentPopupWorkflowTargets(task, workflow)
    const selectableSteps = getCommentPopupSelectableSteps(task, workflow)
    const currentStepId = task?.stepHistory?.[task.stepHistory.length - 1]
    const chatAppearance = appearance === CHAT_APPEARANCE

    useEffect(() => {
        submittingRef.current = false
        setSubmitting(false)
        setSelectorOpen(false)
    }, [currentStepId, task?.done])

    if (!targets) return null

    const currentStepLabel = getWorkflowStepName(workflow, targets.currentStepId)
    const backwardStepName = getWorkflowStepName(workflow, targets.backwardStepId)
    const forwardStepName = getWorkflowStepName(workflow, targets.forwardStepId)

    const moveTaskToStep = async (stepToMoveId, source) => {
        if (disabled || submittingRef.current) return
        // Done has no forward destination (AT-2501). The button is hidden, so this only guards the
        // keyboard/programmatic route into `moveTask`.
        if (stepToMoveId === null || stepToMoveId === undefined) return

        submittingRef.current = true
        setSubmitting(true)
        setSelectorOpen(false)

        try {
            // AT-2495 — the popup header renders the real task row, so completing from here plays
            // the same motion the checkbox does. The header row is RETAINED (there is no list to
            // leave), so a plain step advance draws nothing and the hook answers a zero hold —
            // the forward/backward buttons stay exactly as immediate as they are today.
            await completeTaskWithMotion(completionMotion, { isCompletion: stepToMoveId === DONE_STEP }, async () => {
                return runWithLoading('move_commented_task', async () => {
                    // AT-2501 — reopening a completed task is its own transition, and it is the one
                    // the other two movers cannot express: only `moveTasksFromDone` clears `done` /
                    // `inDone`, restores the due date and reverses the completion statistics. Same
                    // three-way split `StatusPickerStepItem` and `WorkflowEstimation` already use.
                    if (task.done) {
                        await moveTasksFromDone(projectId, task, stepToMoveId)
                    } else if (task.userIds.length === 1) {
                        await moveTasksFromOpen(
                            projectId,
                            task,
                            stepToMoveId,
                            null,
                            null,
                            task.estimations,
                            checkBoxIdRef.current
                        )
                    } else {
                        await moveTasksFromMiddleOfWorkflow(
                            projectId,
                            task,
                            stepToMoveId,
                            null,
                            null,
                            task.estimations,
                            checkBoxIdRef.current
                        )
                    }
                })
            })
            return true
        } catch (error) {
            console.error('[CommentPopupWorkflowControls] Could not move task', {
                projectId,
                taskId: task.id,
                source,
                stepToMoveId,
                error,
            })
            submittingRef.current = false
            setSubmitting(false)
            return false
        }
    }

    const moveTask = async direction => {
        const transitionSucceeded = await moveTaskToStep(
            direction === WORKFLOW_BACKWARD ? targets.backwardStepId : targets.forwardStepId,
            direction
        )
        if (transitionSucceeded) onDirectionalTransitionSuccess?.()
    }

    const controlsDisabled = disabled || submitting

    return (
        <View
            testID="comment-popup-workflow-controls"
            style={[localStyles.container, chatAppearance && localStyles.chatContainer]}
        >
            <TouchableOpacity
                testID="comment-popup-workflow-selector"
                style={[
                    localStyles.selectorButton,
                    chatAppearance && localStyles.chatSelectorButton,
                    chatAppearance && selectorHovered && !controlsDisabled && localStyles.chatSelectorButtonHover,
                    narrow && localStyles.narrowSelectorButton,
                    controlsDisabled && localStyles.disabled,
                ]}
                onPress={() => setSelectorOpen(open => !open)}
                onMouseEnter={onSelectorHover}
                onMouseLeave={offSelectorHover}
                disabled={controlsDisabled}
                accessibilityRole="button"
                accessibilityLabel={translate('Select workflow step with name', { step: currentStepLabel })}
                accessibilityState={{ expanded: selectorOpen, disabled: controlsDisabled }}
            >
                {submitting ? (
                    <ActivityIndicator
                        testID="workflow-transition-loading"
                        size="small"
                        color={chatAppearance ? colors.Primary100 : colors.Text03}
                    />
                ) : (
                    <Icon name="list" size={18} color={chatAppearance ? colors.Text02 : colors.Text03} />
                )}
                <Text
                    style={[
                        styles.subtitle2,
                        localStyles.selectorButtonText,
                        chatAppearance && localStyles.chatSelectorButtonText,
                    ]}
                    numberOfLines={narrow ? 2 : 1}
                >
                    {translate('Current workflow step with name', { step: currentStepLabel })}
                </Text>
                <Icon
                    name={selectorOpen ? 'chevron-up' : 'chevron-down'}
                    size={18}
                    color={chatAppearance ? colors.Text02 : colors.Text03}
                />
            </TouchableOpacity>
            {selectorOpen && (
                <ScrollView
                    testID="comment-popup-workflow-step-list"
                    style={[localStyles.stepList, chatAppearance && localStyles.chatStepList]}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                >
                    {selectableSteps.map(step => (
                        <WorkflowStepOption
                            key={step.id}
                            step={step}
                            controlsDisabled={controlsDisabled}
                            moveTaskToStep={moveTaskToStep}
                            chatAppearance={chatAppearance}
                            narrow={narrow}
                        />
                    ))}
                </ScrollView>
            )}
            <MainButtons
                onDonePress={moveTask}
                selectedCustomStep={false}
                currentStep={targets.currentStep}
                disabled={controlsDisabled}
                shortcutsEnabled={false}
                compact
                // Keep task-detail actions side by side while the selector retains its narrow layout.
                narrow={narrow && !chatAppearance}
                backwardStepName={backwardStepName}
                forwardStepName={forwardStepName}
                hideForwardButton={targets.forwardStepId == null}
            />
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        backgroundColor: colors.Secondary400,
        paddingHorizontal: 16,
        paddingTop: 8,
        width: '100%',
    },
    chatContainer: {
        backgroundColor: colors.Grey100,
        borderBottomColor: colors.Gray300,
        borderBottomWidth: 1,
        paddingBottom: 8,
    },
    selectorButton: {
        alignItems: 'center',
        backgroundColor: colors.Secondary250,
        borderRadius: 4,
        flexDirection: 'row',
        height: 36,
        marginBottom: 8,
        paddingHorizontal: 10,
        width: '100%',
    },
    chatSelectorButton: {
        backgroundColor: 'transparent',
        borderColor: colors.Gray300,
        borderWidth: 1,
    },
    chatSelectorButtonHover: {
        backgroundColor: colors.Grey200,
    },
    selectorButtonText: {
        color: 'white',
        flex: 1,
        marginHorizontal: 8,
        minWidth: 0,
    },
    chatSelectorButtonText: {
        color: colors.Text02,
    },
    disabled: {
        opacity: 0.5,
    },
    narrowSelectorButton: {
        height: 'auto',
        minHeight: 44,
        paddingVertical: 6,
    },
    stepList: {
        backgroundColor: colors.Secondary250,
        borderRadius: 4,
        marginBottom: 8,
        maxHeight: 168,
        paddingHorizontal: 8,
    },
    chatStepList: {
        backgroundColor: colors.Grey100,
        borderColor: colors.Gray300,
        borderWidth: 1,
    },
    stepOption: {
        alignItems: 'center',
        borderBottomColor: colors.Secondary200,
        borderBottomWidth: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        minHeight: 40,
        paddingHorizontal: 8,
        paddingVertical: 8,
    },
    chatStepOption: {
        borderBottomColor: colors.Gray300,
    },
    chatStepOptionHover: {
        backgroundColor: colors.Grey200,
    },
    stepOptionText: {
        color: 'white',
        flex: 1,
        minWidth: 0,
    },
    chatStepOptionText: {
        color: colors.Text02,
    },
    currentStepOption: {
        backgroundColor: colors.Secondary200,
    },
    chatCurrentStepOption: {
        backgroundColor: colors.UtilityBlue100,
    },
    currentStepIndicator: {
        alignItems: 'center',
        flexDirection: 'row',
        marginLeft: 8,
    },
    currentStepText: {
        color: colors.Primary100,
        marginLeft: 4,
    },
})
